"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeContainerClock = computeContainerClock;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const EMPTY_CLOCK = {
    limits: [],
    clockStartDate: null,
    effectiveDeadlineDate: null,
};
function addUtcDays(start, days) {
    return new Date(start.getTime() + days * MS_PER_DAY);
}
function toUtcMidnight(date) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}
// Floored to UTC midnight so persisted clockStartDate/deadlineDate are
// date-pure and SQL date predicates agree with the read-time status bucket.
// The satellite move-window deadline (area.moveDeadline) is a genuine
// timestamp owned by the area rollup and is deliberately NOT floored here.
function resolveClockAnchorDate(container, area) {
    const anchor = resolveRawClockAnchorDate(container, area);
    return anchor === null ? null : toUtcMidnight(anchor);
}
function resolveRawClockAnchorDate(container, area) {
    if (container.isPermanent) {
        if (container.isFlowThrough) {
            return container.lastEmptiedAt ?? container.accumulationStartDate;
        }
        // No fallback: null means no ADDs in the current cycle, so an empty tank
        // has no clock. The caller's deriver returns the first ADD when the tank
        // has never been emptied.
        return container.oldestAddAfterLastEmpty;
    }
    if (area?.type === "CENTRAL") {
        // A container onboarded directly into central has no move stamp; its
        // accumulation start is the date it entered central.
        return container.movedToCentralDate ?? container.accumulationStartDate;
    }
    return container.accumulationStartDate;
}
function buildRuleQuery(container, area, facility) {
    return {
        state: facility.state,
        generatorStatus: facility.generatorStatus,
        areaType: area.type,
        mustTransport200Miles: container.sqgOver200Miles,
        stateGeneratorStatus: facility.stateGeneratorStatus,
        classification: container.classification,
    };
}
// Matches the retired rule records' basis strings: federal central rules were
// written per generator tier; state rules decide the tier internally, which
// the records rendered as "any generator".
function timeBasisLabel(limit, query) {
    if (limit.jurisdiction === "FEDERAL" && query.generatorStatus !== null) {
        return query.generatorStatus;
    }
    return "any generator";
}
function buildVolumeLimit(caps) {
    return {
        limitType: "VOLUME",
        basis: `SAA ${caps.nonAcuteGal} gal / ${caps.acuteGal} gal (${caps.acuteKg} kg) acute`,
        citation: caps.citation,
        jurisdiction: caps.jurisdiction,
        deadlineDate: null,
        totalDays: null,
        isMoveWindow: false,
    };
}
function buildMoveWindowLimit(caps, moveDeadline) {
    return {
        limitType: "TIME",
        basis: `SAA ${caps.moveWindowDays}-day move window`,
        citation: caps.citation,
        jurisdiction: caps.jurisdiction,
        deadlineDate: moveDeadline,
        totalDays: caps.moveWindowDays,
        isMoveWindow: true,
    };
}
function buildTimeLimit(limit, query, anchor) {
    return {
        limitType: "TIME",
        basis: `${limit.days} days — ${timeBasisLabel(limit, query)}, ${query.areaType.toLowerCase()}`,
        citation: limit.citation,
        jurisdiction: limit.jurisdiction,
        deadlineDate: addUtcDays(anchor, limit.days),
        totalDays: limit.days,
        isMoveWindow: false,
    };
}
function computeContainerClock(container, area, facility, rules) {
    if (container.status === "SHIPPED" || container.archivedAt !== null || area === null) {
        return EMPTY_CLOCK;
    }
    const anchor = resolveClockAnchorDate(container, area);
    const query = buildRuleQuery(container, area, facility);
    const resolved = rules(query);
    const limits = [];
    if (resolved.areaType === "SATELLITE" && resolved.caps.kind === "VOLUME") {
        limits.push(buildVolumeLimit(resolved.caps));
        if (area.isOverThreshold && area.moveDeadline !== null) {
            limits.push(buildMoveWindowLimit(resolved.caps, area.moveDeadline));
        }
    }
    // The anchor, not the generator status, gates the deadline: a state's
    // standing satellite clock (CA's one-year limit) resolves even with no
    // resolvable status, while a null anchor means no ADDs in the current
    // cycle — nothing to date a deadline from.
    if (resolved.clock.kind === "TIME" && anchor !== null) {
        limits.push(buildTimeLimit(resolved.clock, query, anchor));
    }
    const timeDeadlines = limits
        .map((limit) => limit.deadlineDate)
        .filter((deadline) => deadline !== null);
    const effectiveDeadlineDate = timeDeadlines.length > 0
        ? new Date(Math.min(...timeDeadlines.map((deadline) => deadline.getTime())))
        : null;
    return {
        limits,
        clockStartDate: anchor,
        effectiveDeadlineDate,
    };
}
//# sourceMappingURL=computeContainerClock.js.map